import { apiClient } from '../config';
import { endpoints } from '../endpoints';

/**
 * Volume service for handling volume operations
 */
export const volumeService = {
  /**
   * Get all volumes for a journal
   * @param {string} journalId - Journal ID
   * @returns {Promise<Array>} - Promise resolving to array of volumes
   */
  getVolumesByJournal: async (journalId) => {
    const response = await apiClient.get(endpoints.volumes.getByJournal(journalId));
    return response.data;
  },

  /**
   * Get volume by ID
   * @param {string} id - Volume ID
   * @returns {Promise<Object>} - Promise resolving to volume data
   */
  getVolumeById: async (id) => {
    const response = await apiClient.get(endpoints.volumes.getById(id));
    return response.data.data;
  },

  /**
   * Create a new volume
   * @param {string} journalId - Journal ID
   * @param {Object} volumeData - Volume data
   * @param {number} volumeData.number - Volume number
   * @param {number} volumeData.year - Volume year
   * @param {string} volumeData.title - Volume title
   * @param {string} volumeData.description - Volume description
   * @param {string} volumeData.status - Volume status
   * @returns {Promise<Object>} - Promise resolving to created volume
   */
  createVolume: async (journalId, volumeData) => {
    const response = await apiClient.post(endpoints.volumes.create(journalId), volumeData);
    return response.data.data;
  },

  /**
   * Update an existing volume
   * @param {string} id - Volume ID
   * @param {Object} volumeData - Volume data to update
   * @param {number} [volumeData.number] - Volume number
   * @param {number} [volumeData.year] - Volume year
   * @param {string} [volumeData.title] - Volume title
   * @param {string} [volumeData.description] - Volume description
   * @param {string} [volumeData.status] - Volume status
   * @returns {Promise<Object>} - Promise resolving to updated volume
   */
  updateVolume: async (id, volumeData) => {
    const response = await apiClient.put(endpoints.volumes.update(id), volumeData);
    return response.data.data;
  },

  /**
   * Delete a volume
   * @param {string} id - Volume ID
   * @returns {Promise<Object>} - Promise resolving to success message
   */
  deleteVolume: async (id) => {
    const response = await apiClient.delete(endpoints.volumes.delete(id));
    return response.data;
  },

  /**
   * Upload a file for a volume
   * @param {string} volumeId - Volume ID
   * @param {File} file - File to upload
   * @returns {Promise<Object>} - Promise resolving to updated volume
   */
  uploadVolumeFile: async (volumeId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await apiClient.post(
      endpoints.volumes.uploadFile(volumeId),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );

    return response.data;
  },

  /**
   * Get the download URL for a volume file
   * @param {string} volumeId - Volume ID
   * @returns {string} - URL for downloading the volume file
   */
  getVolumeFileDownloadUrl: (volumeId) => {
    return endpoints.volumes.downloadFile(volumeId);
  }
};
